import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';

import type {
  ComponentId,
  ComponentType,
  Layout,
} from '@vgtv/api-client/lib/component';
import type { DateISO, Provider } from '@vgtv/api-client/lib/common';
import type { UserIdentifier } from '@vgtv/api-client/lib/user';
import { Link, useHistory } from 'react-router-dom';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';

import { Slider } from '../../../Slider/Slider';
import { Image } from '../../../Image/Image';
import { getImageSources } from '../../../../helpers/images';
import { Overlay } from '../../../Overlay/Overlay';
import { CardsGallery as DesktopCardsGallery } from '../../../CardsGallery/CardsGallery';
import { CardsGallery as MobileCardsGallery } from '../../../CardsGallery/Mobile/CardsGallery';
import { useQueryParams } from '../../../utils/useQueryParams';
import { useMediaQuery } from '../../../utils/useMediaQuery';
import { useViewObserver } from '../../../utils/useViewObserver';
import { getSlug } from '../../../../shared/helpers/getSlug';
import { selectCategoryById } from '../../../../modules/categories';
import { useAppSelector } from '../../../../modules/hooks';
import { useVendor } from '../../../VendorPovider/VendorPovider';

import { useTracking } from './useTracking';

import * as styles from './CardsSlider.module.scss';

export interface CardSliderComponent {
  id: ComponentId;
  name?: string;
  categoryId: SvpCategoryId;
  provider: Provider;
  createdAt: DateISO;
  updatedAt: DateISO;
  updatedBy: UserIdentifier;
  isShared: boolean;
  sharedName?: string;
  type: ComponentType.SLIDER;
  header?: string;
  options: {
    useVerticalPosters?: never;
    sortByFlightTimes?: never;
    sortByEpisodeNumber?: never;
    usePageTaxonomy: boolean;
    layout: Layout;
    hasSingleItem?: never;
    hideSearchField?: never;
    placeholder?: never;
    isFullFledged?: never;
    buttonLabel?: never;
  };
  data: {
    type: 'cards';
    preset: { name: string; images: { thumbnail: string; card: string } }[];
  };
}

export interface CardsSliderProps {
  componentConfig: CardSliderComponent;
  pageComponentIndex: number;
}

const QUERY_PARAM = 'deltaker';

export const CardsSlider: FC<CardsSliderProps> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const {
    header,
    data: { preset },
    categoryId,
  } = componentConfig;

  const { vendor } = useVendor();

  const category = useAppSelector((state) =>
    selectCategoryById(state, vendor, categoryId)
  );

  const ref = useRef<HTMLDivElement>(null!);
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const { [QUERY_PARAM]: participant } = useQueryParams<typeof QUERY_PARAM>();

  const [selectedIndex, setSelectedIndex] = useState(() =>
    preset.findIndex(({ name }) => getSlug(name) === participant)
  );

  const { trackSliderView, trackCardClick } = useTracking({
    elementType: 'CardsSlider',
    pageComponentIndex,
    header,
    category,
  });

  useViewObserver({
    onView: trackSliderView,
    elementRef: ref,
  });

  useEffect(() => {
    const selectedItem = preset[selectedIndex];

    const params = new URLSearchParams(history.location.search);

    if (selectedItem) {
      params.set(QUERY_PARAM, getSlug(selectedItem.name));
    } else {
      params.delete(QUERY_PARAM);
    }

    history.replace({ search: params.toString() });
  }, [history, selectedIndex, preset]);

  const handleCloseButtonClick = useCallback(() => {
    setSelectedIndex(-1);
  }, []);

  const cards = useMemo(
    () =>
      preset.map(({ name, images }) => {
        return {
          name,
          image: images.card,
        };
      }),
    [preset]
  );

  const CardsGallery = isMobile ? MobileCardsGallery : DesktopCardsGallery;

  return (
    <>
      <Slider
        header={header}
        itemClassName={styles.item}
        className={styles.slider}
        ref={ref}
      >
        {preset.map(({ name, images }, index) => (
          <Link
            key={index}
            to={() => {
              const params = new URLSearchParams(history.location.search);

              params.set(QUERY_PARAM, getSlug(name));

              return `?${params.toString()}`;
            }}
            onClick={() => {
              trackCardClick({
                name,
                position: index,
              });

              setSelectedIndex(index);
            }}
          >
            <div className={styles.thumbnail}>
              <Image
                imageSources={getImageSources({
                  imageUrl: images.thumbnail,
                  aspectRatio: '61/100',
                  imagePreset: 'assetsSliderVertical',
                })}
              />
              <div className={styles.label}>{name}</div>
            </div>
          </Link>
        ))}
      </Slider>
      <Overlay
        contentClassName={styles.overlay}
        isOpen={selectedIndex !== -1}
        onCloseButtonClick={handleCloseButtonClick}
      >
        <CardsGallery
          cards={cards}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={setSelectedIndex}
        />
      </Overlay>
    </>
  );
};
